'use client';

import styled from '@emotion/styled';
import { Css, ITheme, Responsive } from '../../../ui';
import { Color } from '../../../styles/Color';
import TextArrowButton from '../button/TextArrowButton';

interface ICarouselNavigationProps {
	currentIndex: number;
	totalItems: number;
	onPrev: () => void;
	onNext: () => void;
	onDotClick: (index: number) => void;
	getKey: (index: number) => string;
	previousLabel?: string;
	nextLabel?: string;
}

export default function CarouselNavigation({
	currentIndex,
	totalItems,
	onPrev,
	onNext,
	onDotClick,
	getKey,
	previousLabel,
	nextLabel,
}: ICarouselNavigationProps) {
	return (
		<NavigationSection>
			<NavigationContainer>
				<TextArrowButton
					disabled={currentIndex === 0}
					aria-disabled={currentIndex === 0}
					aria-label={previousLabel}
					onClick={onPrev}
					iconPosition="left"
				>
					{previousLabel}
				</TextArrowButton>

				<DotsContainer>
					{Array.from({ length: totalItems }, (_, index) => (
						<Dot
							key={getKey(index)}
							active={currentIndex === index}
							onClick={() => onDotClick(index)}
						/>
					))}
				</DotsContainer>

				<TextArrowButton
					disabled={currentIndex === totalItems - 1}
					aria-disabled={currentIndex === totalItems - 1}
					aria-label={nextLabel}
					onClick={onNext}
				>
					{nextLabel}
				</TextArrowButton>
			</NavigationContainer>
		</NavigationSection>
	);
}

const NavigationSection = styled.div`
	position: relative;
	padding: 0;
`;

const NavigationContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: ${Css.pxToRem(40)};
	margin: 0 20px;

	@media (${Responsive.getMediaQueryForBreakpoint('desktopXL')}) {
		margin: 0;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tabletXL')}) {
		margin: 0;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		justify-content: space-between;
		gap: 0;
	}
`;

const DotsContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
`;

const Dot = styled.div<{ active: boolean }, ITheme>`
	width: ${Css.pxToRem(8)};
	height: ${Css.pxToRem(8)};
	border-radius: 50%;
	background-color: ${({ active, theme }) =>
		active ? theme.color.secondaryLight : Color.Neutral3};
	border: 1px solid
		${({ active, theme }) =>
			active ? theme.color.secondaryLight : Color.Neutral3};
	margin: 0 3px;
	cursor: pointer;

	&:hover {
		background-color: ${({ theme }) => theme.color.secondaryLighter};
	}
`;
